<template>
  <main id="content" aria-labelledby="main-title" v-if="page">
    
    <article>
      
      <ElementHeroBlog :content="page" />

      <Container class="px-0 pl-4 sm:pl-8 sm:px-0 md:px-8">
        <div class="md:grid md:grid-cols-12 lg:grid-cols-12 gap-8 relative">
          
          <div class="md:col-span-7 relative">
            <div class="prose pt-12">
              <p>
                <strong>Date:</strong> {{ useFormattedDate(page.date) }}<br>
                <strong>Time:</strong> {{ page.time }} {{ useAustralianTimezone(page.date) }}<br>
                <strong>Duration:</strong> {{ page.duration }}<br>
                <strong>Cost:</strong> Free (Limited places)
              </p>
              <p v-if="page.brochure"><strong>Download brochure:</strong> <a target="_blank" :href="useCdn(page.brochure.id, page.brochure.filename_download)">Click here</a></p>
            </div>
            <div 
            class="prose relative pt-12 pb-12" 
            v-interpolation 
            v-html="useSanitize(page.content, currentLocale)"
            ></div>
          </div>

          <div class="md:col-span-5 relative pt-8 pb-16">
            <div class="webinar-box p-4 md:p-6 rounded-[10px] md:rounded-[20px] bg-[#e8e5de]">

              <template v-if="pastWebinar">

                <template v-if="page?.recording_url || page?.recording_embed">
                  <h2>Watch the video replay</h2>

                  <!-- Recording unlocked: show confirmation + embed/link -->
                  <div v-if="recordingUnlocked">
                    <!-- Email was sent (recording_url exists) -->
                    <div v-if="page.recording_url" class="bg-white rounded-lg p-4 mb-6">
                      <div class="flex items-start gap-3">
                        <svg class="w-6 h-6 text-[#096b72] flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                        </svg>
                        <div>
                          <p class="font-semibold text-[#096b72]">Check your email</p>
                          <p class="text-sm text-gray-600 mt-1">We've sent a link to the recording to your email address.<template v-if="page.recording_embed"> You can also watch below.</template></p>
                        </div>
                      </div>
                    </div>
                    <!-- No email, just showing embed -->
                    <p v-else class="my-2">Thank you. You can watch the video below.</p>
                    <!-- Show embed if available -->
                    <div v-if="page.recording_embed" v-html="page.recording_embed"></div>
                  </div>

                  <!-- Recording gate form -->
                  <div v-else>
                    <form action="#" method="POST" class="mx-auto max-w-xl">
                      <p>Enter your details to receive access to the recording.</p>

                      <!-- Honeypot field (hidden) -->
                      <div style="position: absolute; left: -9999px;">
                        <input type="text" v-model="website" name="website" tabindex="-1" autocomplete="off" />
                      </div>

                      <div class="grid grid-cols-1 gap-x-8 gap-y-2 sm:grid-cols-2">
                        <div class="mt-4 sm:col-span-2">
                          <div>
                            <input
                            type="text"
                            placeholder="First name"
                            v-model="firstname" @focus="recordingTracking.start()"
                            name="firstname"
                            id="recording-firstname"
                            autocomplete="given-name"
                            class="input-text" />
                          </div>
                        </div>
                        <div class="sm:col-span-2">
                          <div>
                            <input
                            type="text"
                            placeholder="Last name"
                            v-model="lastname" @focus="recordingTracking.start()"
                            name="lastname"
                            id="recording-lastname"
                            autocomplete="family-name"
                            class="input-text" />
                          </div>
                        </div>
                        <div class="sm:col-span-2">
                          <div>
                            <input
                            type="text"
                            placeholder="Organisation"
                            v-model="organization" @focus="recordingTracking.start()"
                            id="recording-organization"
                            autocomplete="organization"
                            class="input-text" />
                          </div>
                        </div>
                        <div class="sm:col-span-2">
                          <div>
                            <input
                            type="email"
                            placeholder="Email"
                            v-model="email" @focus="recordingTracking.start()"
                            id="recording-email"
                            autocomplete="email"
                            class="input-text" />
                          </div>
                        </div>
                        <div class="sm:col-span-2">
                          <div class="flex">
                            <input
                            @click="errorMessage = null"
                            id="recording-consent"
                            v-model="consent"
                            type="checkbox"
                            class="h-5 w-5 mt-[3px] cursor-pointer rounded border-[#E4E2DF] text-[#4d5ceb] focus:ring-transparent"
                            />
                            <label @click="errorMessage = null" for="recording-consent" class="ml-3 block text-sm cursor-pointer">
                              I confirm I have read and agree to the processing of my above personal data as described in the <NuxtLink :to="$localePath('/policies/privacy')" class="underline" target="_blank">Privacy Policy</NuxtLink>.
                            </label>
                          </div>
                        </div>
                      </div>
                      <div class="mt-6">
                        <button
                        type="submit"
                        :disabled="waiting"
                        @click="submitRecordingForm"
                        class="w-full inline-flex items-center justify-center rounded-md border border-transparent bg-[#096b72] px-5 py-3 text-md font-semibold text-white hover:bg-[#07484b]"
                        :class="{ 'opacity-60' : waiting }"
                        >
                          <template v-if="waiting">
                            Sending...
                          </template>
                          <template v-else>
                            Get Recording
                          </template>
                        </button>
                      </div>
                      <div class="mt-6" v-if="errorMessage"><p class="text-[#DE1A1A]">{{ errorMessage }}</p></div>
                    </form>
                  </div>
                </template>
                <template v-else>
                  <h2>Registrations closed</h2>
                  <p class="my-2">Please <NuxtLink :to="$localePath('/contact')">contact us</NuxtLink> if you would like a recording of the webinar.</p>
                  <NuxtLink
                  :to="$localePath('/webinars')"
                  class="block w-full flex-none center rounded-md bg-[#4d5ceb] hover:bg-[#3645d0] px-3.5 py-2 mt-6 text-lg font-semibold text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#5a6ded]"
                  >
                    See upcoming webinars
                  </NuxtLink>
                </template>
              </template>

              <template v-else-if="page?.disableRegistration">
                <h2>Register now</h2>
                <p class="my-2">Secure your place. Register with our webinar partner.</p>
                <a
                v-if="page?.extLink"
                target="_blank"
                :href="page.extLink"
                type="submit"
                class="block w-full flex-none center rounded-md bg-[#4d5ceb] hover:bg-[#3645d0] px-3.5 py-2.5 text-lg font-semibold text-white hover:bg-[#3645d0] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#5a6ded]"
                >
                  Go to <template v-if="page.partnerName">{{ page.partnerName }}</template><template v-else>partner</template> page ->
                </a>
              </template>

              <template v-else-if="page?.date === '2024-11-20'">
                <h2>Registration info</h2>
                <p>This session has been limited to a small, private group due to the sensitive nature of some of the topics. If you would like to attend, please email <a style="text-decoration: underline" href="mailto:jack@elker.com">jack@elker.com</a> with you name and organisation.</p>
              </template>
              
              <template v-else>
                <div v-if="sent">
                  <h2>Success</h2>
                  <template v-if="page.livestormId">
                    <p>Thanks. Your registration is successful. Please check your email for the confirmation.</p>
                    <p>If you can't see your confirmation email, check your email's spam folder or <NuxtLink :to="$localePath('/contact')">get in touch with us</NuxtLink>.</p><br>
                  </template>
                  <template v-else>
                    <p>Thanks. Your webinar registration is successful. We will be in touch shortly by email with webinar access details.</p><br>
                  </template>
                  <p>
                    <strong>Date:</strong> {{ useFormattedDate(page.date) }}<br>
                    <strong>Time:</strong> {{ page.time }} {{ useAustralianTimezone(page.date) }}<br>
                    <strong>Duration:</strong> {{ page.duration }}<br>
                    <strong>Cost:</strong> Free (Limited places)
                  </p>
                </div>
                <form v-else action="#" method="POST" class="mx-auto max-w-xl">
                  <h2>Register now</h2>
                  <p>Secure your place. Limited spots available.</p><br>
                  <p>
                    <strong>Date:</strong> {{ useFormattedDate(page.date) }}<br>
                    <strong>Time:</strong> {{ page.time }} {{ useAustralianTimezone(page.date) }}<br>
                    <strong>Duration:</strong> {{ page.duration }}<br>
                    <strong>Cost:</strong> Free (Limited places)
                  </p>
                  <div class="grid grid-cols-1 gap-x-8 gap-y-2 sm:grid-cols-2">
                    <div class="mt-4 sm:col-span-2">
                      <div>
                        <input 
                        type="text" 
                        placeholder="First name" 
                        v-model="firstname" @focus="registerTracking.start()" 
                        name="firstname" 
                        id="firstname" 
                        autocomplete="given-name"
                        class="input-text" />
                      </div>
                    </div>
                    <div class="sm:col-span-2">
                      <div>
                        <input 
                        type="text" 
                        placeholder="Last name" 
                        v-model="lastname" @focus="registerTracking.start()" 
                        name="lastname" 
                        id="lastname" 
                        autocomplete="family-name"
                        class="input-text" />
                      </div>
                    </div>
                    <div class="sm:col-span-2">
                      <div>
                        <input 
                        type="text" 
                        placeholder="Organisation" 
                        v-model="organization" @focus="registerTracking.start()" 
                        id="organization" 
                        autocomplete="organization" 
                        class="input-text" />
                      </div>
                    </div>
                    <div class="sm:col-span-2">
                      <div>
                        <input
                        type="email"
                        placeholder="Email"
                        v-model="email" @focus="registerTracking.start()"
                        id="email"
                        autocomplete="email"
                        class="input-text" />
                      </div>
                    </div>
                    <!-- Honeypot field - hidden from users, bots will fill it -->
                    <div aria-hidden="true" style="position: absolute; left: -9999px;">
                      <label for="register-website">Website</label>
                      <input type="text" v-model="website" id="register-website" name="website" tabindex="-1" autocomplete="off" />
                    </div>
                    <div class="sm:col-span-2">
                      <div class="flex items-center">
                        <input
                        @click="errorMessage = null"
                        id="consent"
                        v-model="consent"
                        type="checkbox"
                        class="h-5 w-5 mt-[5px] rounded border-[#E4E2DF] text-[#4d5ceb] focus:ring-transparent"
                        />
                        <label @click="errorMessage = null" for="consent" class="ml-3 block text-sm">
                          <span v-if="page?.extLink">
                            I confirm I have read and agree to the processing of my above personal data as described in the <NuxtLink :to="$localePath('/policies/privacy')" class="underline" target="_blank">Privacy Policy</NuxtLink> and I consent to share my registration details with <span v-if="page.partnerName">{{ page.partnerName }}</span><span v-else>the event partner</span>.
                          </span>
                          <span v-else>
                            I confirm I have read and agree to the processing of my above personal data as described in the <NuxtLink :to="$localePath('/policies/privacy')" class="underline" target="_blank">Privacy Policy</NuxtLink>.
                          </span>
                        </label>
                      </div>
                    </div>
                  </div>
                  <div class="mt-6">
                    <button 
                    type="submit" 
                    :disabled="waiting" 
                    @click="submitForm" 
                    class="w-full inline-flex items-center justify-center rounded-md border border-transparent bg-[#096b72] px-5 py-3 text-md font-semibold text-white hover:bg-[#07484b]"
                    :class="{ 'opacity-60' : waiting }"
                    >
                      <template v-if="waiting">
                        Sending
                      </template>
                      <template v-else>
                        Register
                      </template>
                    </button>
                  </div>
                  <div class="mt-6" v-if="errorMessage"><p class="text-[#DE1A1A]">{{ errorMessage }}</p></div>
                </form>
              </template>

            </div>
          </div>
    
        </div>
      </Container>

      <Container>
        <div class="md:grid md:grid-cols-12 lg:grid-cols-12 gap-8 relative">  
          <div class="md:col-span-9 relative">
            <div
            v-if="page.presenters"
            class="prose">
              <h2 class="lora">Presenters</h2>
              <ul role="list" class="pl-0 space-y-4 md:space-y-8 divide-y divide-[#E4E2DF] xl:col-span-3">
                <li v-for="person in page.presenters" class="flex flex-col gap-4 md:gap-10 pt-6 md:pt-8 sm:flex-row">
                  <img 
                  class="my-0 aspect-[5/5] rounded-full w-24 h-24 md:w-36 md:h-36 flex-none object-cover" 
                  :src="useCdn(person.person_id.image.id, person.person_id.filename_download)" :alt="`${person.person_id.title} - ${person.person_id.position}, ${ person.person_id.org ? person.person_id.org : 'Elker' }`"
                  loading="lazy" 
                  />
                  <div class="max-w-xl flex-auto">
                    <h3 class="my-0">{{ person.person_id.title }}</h3>
                    <h4 class="">{{ person.person_id.position }}, {{ person.person_id.org ? person.person_id.org : 'Elker' }}</h4>
                    <p class="mt-2 text-base leading-6 text-gray-600">{{ person.person_id.subtitle }}</p>
                    <ul 
                    v-if="person.person_id.linkedinUrl"
                    role="list" 
                    class="pl-0 m-0 flex gap-x-6" 
                    style="list-style: none">
                      <li class="pl-0">
                        <a target="_blank" :href="person.person_id.linkedinUrl" class="text-gray-400 hover:text-gray-500">
                          <span class="sr-only">LinkedIn</span>
                          <svg class="h-6 w-6" aria-hidden="true" fill="currentColor" viewBox="0 0 20 20">
                            <path fill-rule="evenodd" d="M16.338 16.338H13.67V12.16c0-.995-.017-2.277-1.387-2.277-1.39 0-1.601 1.086-1.601 2.207v4.248H8.014v-8.59h2.559v1.174h.037c.356-.675 1.227-1.387 2.526-1.387 2.703 0 3.203 1.778 3.203 4.092v4.711zM5.005 6.575a1.548 1.548 0 11-.003-3.096 1.548 1.548 0 01.003 3.096zm-1.337 9.763H6.34v-8.59H3.667v8.59zM17.668 1H2.328C1.595 1 1 1.581 1 2.298v15.403C1 18.418 1.595 19 2.328 19h15.34c.734 0 1.332-.582 1.332-1.299V2.298C19 1.581 18.402 1 17.668 1z" clip-rule="evenodd" />
                          </svg>
                        </a>
                      </li>
                    </ul>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </Container>

    </article>

    
    <Section class="mt-16 bg-faun" background="#e8e5de" v-if="upcomingWebinarsReverse">
      <Container>
        <FadeIn delay="200">
          <h2 class="text-h2 mb-8">Upcoming webinars</h2>
          <div class="mx-auto mt-0 grid grid-cols-1 gap-x-8 gap-y-16 lg:mx-0 lg:max-w-none lg:grid-cols-3">
            <ElementWebinarCard 
            v-for="(article, index) in upcomingWebinarsReverse" 
            :key="'webinar-thumb-'+index" 
            :article="article" 
            type="webinar"
            />
          </div>
        </FadeIn>
      </Container>
    </Section>

    <Section v-else class="mt-16" background="#e8e5de">
      <Container v-if="pastWebinars">
        <FadeIn delay="200">
          <h2 class="text-h2 mb-8">Past webinars</h2>
          <div class="mx-auto mt-0 grid grid-cols-1 gap-x-8 gap-y-16 lg:mx-0 lg:max-w-none lg:grid-cols-3">
            <ElementWebinarCard 
            v-for="(article, index) in pastWebinars" 
            :key="'past-webinar-thumb-'+index" 
            :article="article" 
            type="webinar"
            />
          </div>
        </FadeIn>
      </Container>
    </Section>

    <!-- <ElementLeaveDetails type="newsletter" /> -->
    <ElementBookDemoCta />
    
  </main>
</template>

<script setup>
// Remount on every navigation (its own page key). This page emits JSON-LD via
// useSchemaOrg, whose graph is not cleanly replaced on client-side nav between schema
// pages; remounting re-renders it from scratch. See specs/remove-global-page-key.md.
definePageMeta({ key: route => route.fullPath })

// Publish gate: an unbuilt CMS page 404s until the next deploy prerenders it.
useRequirePrerendered()

const route = useRoute()
const userStore = useUserStore()


const currentLocale = computed(() => {
  return route.path.startsWith('/nz') ? 'nz' : 'au'
})

const sydneyDate = ref(
  new Date().toLocaleString('en-AU', {
    timeZone: 'Australia/Sydney', 
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).split('/').reverse().join('-')
)

const today = new Date(sydneyDate.value)
const todayPlusOne = new Date(today)
todayPlusOne.setDate(today.getDate() - 1)

const sent = ref(false)
const firstname = ref('')
const lastname = ref('')
const email = ref('')
const organization = ref('')
const errorMessage = ref(null)
const waiting = ref(null)
const consent = ref(false)

// Recording gate state
const recordingUnlocked = ref(false)
const website = ref('') // Honeypot field
const formLoadedAt = ref(Date.now())

const { getItems } = useCms()

const { data } = await useAsyncData(
  `webinar-page-${route.params.slug}`,
  async () => {

    const pageData = await getItems({
      collection: 'webinar',
      params: {
        filter: {
          locale: { _contains: currentLocale.value },
          slug: {
            "_eq": route.params.slug
          },
          status: {
            _in: useRuntimeConfig().public.postStatus
          }
        },
        fields: [
          '*',
          'image.id', 'image.filename_download', 'image.description', 'image.width', 'image.height',
          'seo.*',
          'seo.meta_title',
          'seo.meta_description',
          'seo.meta_image.*',
          'brochure.id', 'brochure.filename_download',
          'presenters.person_id.*',
          'presenters.person_id.image.id',
          'presenters.person_id.image.filename_download',
          'presenters.person_id.image.description',
          'presenters.person_id.image.width',
          'presenters.person_id.image.height',
          'webinar_platform',
          'livestormId',
          'zoomWebinarId',
          'recording_url',
          'recording_embed',
        ]
      }
    })

    const articlesData = await getItems({
      collection: 'webinar',
      params: {
        fields: [
          '*', 'image.id', 'image.filename_download', 'image.description', 'image.width', 'image.height'
        ],
        sort: "-date"
      }
    })

    return {
      page: pageData?.[0] || null,
      articles: articlesData
    }
  }
)

// NZ→AU fallback for AU-only webinars. On a full HTTP request the edge middleware
// (nzFallbackRedirect in middleware.ts) 302-redirects /nz/<slug> to the AU URL before
// this page renders. But the edge does not run on client-side (SPA) navigation, so an
// in-app link to /nz/webinars/<au-only-slug> reaches this page with an empty NZ result.
// Cover that here using the already-loaded manifest (no Directus call): if the slug has
// an AU version, redirect to it; otherwise it is a genuine 404.
if (!data.value?.page) {
  const nzSlugs = useState('nzSlugs', () => null)
  const auAvailable = nzSlugs.value?.webinar?.au?.includes(route.params.slug)
  if (currentLocale.value === 'nz' && auAvailable) {
    await navigateTo(`/webinars/${route.params.slug}`, { redirectCode: 302, external: false })
  } else {
    throw createError({
      statusCode: 404,
      message: 'Webinar not found',
      fatal: true
    })
  }
}

const page = computed(() => data?.value?.page)
const articles = computed(() => data?.value?.articles)

const upcomingWebinarsReverse = computed(() => {
  const upcomingWeb = articles.value.filter(item => {
    return item.slug !== route.params.slug && todayPlusOne <= new Date(item.date)
  })
  return upcomingWeb.length ? upcomingWeb.reverse() : null
})
const pastWebinars = computed(() => {
  return articles.value.filter(item => {
    return item.slug !== route.params.slug && todayPlusOne > new Date(item.date)
  })
})


const pastWebinar = computed(() => {
  if (todayPlusOne <= new Date(page.value.date)) {
    return false
  }
  return true
})

// Two distinct funnels on this page: registering for a live webinar, and
// unlocking a past webinar's recording. Tracked separately because they are
// different intents at different stages.
const registerTracking = useFormTracking('webinar-register')
const recordingTracking = useFormTracking('webinar-recording')

const submitForm = async (e) => {
  e.preventDefault()
  waiting.value = true
  errorMessage.value = null
  registerTracking.submitted()

  if (!firstname.value || !email.value || !organization.value) {
    errorMessage.value = 'Please fill out all fields before submitting the form.'
    registerTracking.failed('missing-required-fields')
    waiting.value = null
    return false
  }

  if (!consent.value) {
    errorMessage.value = 'To continue, please confirm we can use the above personal data in accordance with our Privacy Policy.'
    registerTracking.failed('consent-not-given', 'consent')
    waiting.value = null
    return false
  }

  const data = {
    first_name: firstname.value,
    last_name: lastname.value || '',
    email: email.value,
    organization: organization.value,
    message: `<p>User registered for webinar: ${page.value.title} on ${page.value.date}</p>`,
    webformId: '59',
    'marketing_status': 'subscribed',
    utmSource: userStore.utmSource,
    utmMedium: userStore.utmMedium,
    utmCampaign: userStore.utmCampaign,
    utmLandingPage: userStore.utmLandingPage,
    // Conversion attribution for the separate webinar conversion.
    ...useConversionFields(),
    webinar_platform: page.value.webinar_platform || null,
    liveStormSession: page.value.livestormId || null,
    zoomWebinarId: page.value.zoomWebinarId || null,
    // Spam protection (same fields the recording form below already sends)
    website: website.value,
    formLoadedAt: formLoadedAt.value
  }

  try {

    const addUserRes = await $fetch('/api/webinarRegister', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    })

    console.log('API Response:', addUserRes)

    if (addUserRes.ok) {

      sent.value = true
      registerTracking.ok({ recorded: addUserRes.recorded !== false })
      console.log('Registration successful, sent.value is now:', sent.value)

      // recorded: false is a server-side spam decoy. /api/webinarRegister
      // returns it precisely so the client can show success without firing a
      // conversion, and gating only on dataLayer ignored that contract and let
      // every trapped bot push a webinar conversion to the ad engines.
      if (process.client && window.dataLayer && addUserRes.recorded !== false) {

        // Webinar sign-ups are a SEPARATE, lower-value conversion, so fire a
        // distinct event (NOT elker_conversion) to keep them out of the primary
        // lead conversion tag. Server-side (webinarRegister) fires the dedicated
        // webinar conversions to the ad engines.
        window.dataLayer.push({
          event: 'elker_webinar_conversion',
          'enhanced_conversion_email': email.value
        })

        window.dataLayer.push({
          event: 'webinar_registered'
        })

      }

    } else {
      console.log('API returned ok: false')
      errorMessage.value = addUserRes.message || 'Registration failed'
      registerTracking.failed('server-rejected')
    }

    waiting.value = null

  } catch(e) {

    console.error('Registration error:', e)
    waiting.value = null

    if (!sent.value) {
      errorMessage.value = 'There has been an error submitting the form. Please email us at hello@elker.com'
      registerTracking.failed('request-failed')
    }

  }

}

const submitRecordingForm = async (e) => {
  e.preventDefault()
  waiting.value = true
  errorMessage.value = null
  recordingTracking.submitted()

  // Validation
  if (!firstname.value || !email.value) {
    errorMessage.value = 'Please fill out all required fields.'
    recordingTracking.failed('missing-required-fields')
    waiting.value = null
    return false
  }

  if (!consent.value) {
    errorMessage.value = 'To continue, please confirm we can use the above personal data in accordance with our Privacy Policy.'
    recordingTracking.failed('consent-not-given', 'consent')
    waiting.value = null
    return false
  }

  const data = {
    first_name: firstname.value,
    last_name: lastname.value || '',
    email: email.value,
    organization: organization.value,
    webinarTitle: page.value.title,
    recording_url: page.value.recording_url || null,
    utmSource: userStore.utmSource,
    utmMedium: userStore.utmMedium,
    utmCampaign: userStore.utmCampaign,
    utmLandingPage: userStore.utmLandingPage,
    // Conversion attribution for the separate webinar conversion.
    ...useConversionFields(),
    // Spam protection
    website: website.value,
    formLoadedAt: formLoadedAt.value
  }

  try {

    const response = await $fetch('/api/webinarRecordingAccess', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    })

    if (response.ok) {

      recordingUnlocked.value = true
      recordingTracking.ok({ recorded: response.recorded !== false })

      // Same decoy contract as the registration form above:
      // /api/webinarRecordingAccess documents recorded:false as "so a client can
      // show success without firing conversions".
      if (process.client && window.dataLayer && response.recorded !== false) {
        // Separate, lower-value conversion: fire a distinct event (not
        // elker_conversion) so this stays out of the primary lead tag.
        window.dataLayer.push({
          event: 'elker_webinar_conversion',
          'enhanced_conversion_email': email.value
        })
        window.dataLayer.push({
          event: 'webinar_recording_requested'
        })
      }

    } else {
      errorMessage.value = response.message || 'Something went wrong. Please try again.'
      recordingTracking.failed('server-rejected')
    }

  } catch(e) {
    console.error('Recording form error:', e)
    errorMessage.value = 'There has been an error. Please email us at hello@elker.com'
    recordingTracking.failed('request-failed')
  }

  waiting.value = null
}

const seo = computed(() => {
  if (page.value?.seo) {
    return useSeo(page.value.seo, route.fullPath)
  }
  return {}
})

useHead(seo)

const webinarImage = page.value?.seo?.meta_image?.id
  ? useCdn(page.value.seo.meta_image.id, page.value.seo.meta_image.filename_download)
  : null

const webinarUrl = `https://elker.com${route.fullPath}`

const webinarPerformers = (page.value?.presenters || [])
  .map(p => p.person_id)
  .filter(Boolean)
  .map(person => ({
    '@type': 'Person',
    name: person.title,
    jobTitle: person.position,
    ...(person.org ? { worksFor: { '@type': 'Organization', name: person.org } } : {}),
    ...(person.linkedinUrl ? { sameAs: [person.linkedinUrl] } : {}),
    ...(person.image?.id && person.image?.filename_download
      ? { image: useCdn(person.image.id, person.image.filename_download) }
      : {})
  }))

const elkerOrganization = {
  '@type': 'Organization',
  name: 'Elker',
  url: 'https://elker.com',
  logo: 'https://cdn.elker.com/elker_logo_651cfb3fa6.png'
}

useSchemaOrg([
  defineEvent({
    name: page.value?.title,
    description: page.value?.seo?.meta_description || page.value?.subtitle,
    startDate: page.value?.date,
    eventStatus: 'https://schema.org/EventScheduled',
    eventAttendanceMode: 'https://schema.org/OnlineEventAttendanceMode',
    location: {
      '@type': 'VirtualLocation',
      url: webinarUrl
    },
    image: webinarImage,
    organizer: elkerOrganization,
    ...(webinarPerformers.length ? { performer: webinarPerformers } : {}),
    inLanguage: currentLocale.value === 'nz' ? 'en-NZ' : 'en-AU'
  })
])

</script>

<style lang="scss">
.webinar-box {
 h2 {
   font-weight: 700;
   font-size: 1.5em;
   font-size: 24px;
   margin-bottom: 10px;
 }
 p {
   line-height: 1.4;
 }
}
</style>